import React, { useState, Fragment, useEffect, useContext } from 'react';
import { FeedArea } from '@go-shared/types/feedTemplate';
import { createColumn, hotspotTarget, collectTarget, createRow } from '@go-client/lib/feedEditor';
import FeedEditorColumn from './FeedEditorColumn';
import './FeedEditorLayoutComponent.scss';
import { BareContentTemplateLegacy, FeedEditorElementType } from '@go-shared/types/contentTemplate';
import { DropTarget } from 'react-dnd';
import { CONTENT_DND_TYPE } from '@go-client/components/DraggableContentItem';
import { VenuesRundownHotspot } from '../VenueRundown/VenueRundownHotspot';
import { VenueMealPeriodsHotspot } from '../VenueMealPeriods/VenueMealPeriodsHotspot';
import { FeedEditorHotspot } from '@go-client/components';
import { ClickAway } from '@go-client/components/UIkit/ClickAway/ClickAway';
import { Language } from '@go-shared/types/info';
import { BubbleButton } from '@go-client/components/BubbleUI';
import { FeedEditorCtx } from '@go-client/context/FeedEdtitorContext';
import { DailyProgram } from '@go-shared/types/dailyProgram';

interface FeedEditorRowProps {
  parent: FeedArea;
  parentIdx: number;
  childCount?: number;
  onRowCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  onRowMove: (area: FeedArea, idx: number, direction: 1 | -1) => void;
  onColumnCreated: (area: FeedArea, idx: number, newArea: FeedArea) => void;
  onDrop?: (contentTemplate: BareContentTemplateLegacy) => void;
  connectDropTarget?: any;
  isOver?: boolean;
  highlightAreas: boolean;
  onContentChange: (area: FeedArea, contentTemplate: BareContentTemplateLegacy) => void;
  onElementCreated: (area: FeedArea, elementType: FeedEditorElementType) => void;
  onAreaDeleted: (area: FeedArea) => void;
  onElementChanged: (area: FeedArea) => void;
  selectedLanguage: Language | undefined;
  showMergedVars: boolean;
  area?: FeedArea;
  activeElem?: string;
  setActiveElement?: (id: string) => void;
  editorLocked: boolean;
  dailyProgram: DailyProgram;
  mcmKey: string;
}

const FeedEditorRow: React.FC<FeedEditorRowProps> = ({
  parent,
  parentIdx,
  onRowCreated,
  onColumnCreated,
  onRowMove,
  connectDropTarget,
  onContentChange,
  onElementCreated,
  onAreaDeleted,
  onElementChanged,
  selectedLanguage,
  showMergedVars,
  highlightAreas,
  area,
  childCount,
  activeElem,
  setActiveElement,
  editorLocked,
  dailyProgram,
  mcmKey,
}) => {
  const [mouseOver, setMouseOver] = useState(false);
  const [menuVisible, setMenuVisible] = useState(false);
  const { kbEvent } = useContext(FeedEditorCtx);

  useEffect(() => {
    if (activeElem != parent._id) {
      setMenuVisible(false);
    } else {
      setMenuVisible(true);
    }
  }, [activeElem]);

  useEffect(() => {
    if (menuVisible && setActiveElement) {
      setActiveElement(parent._id);
    }
  }, [menuVisible]);

  useEffect(() => {
    if (kbEvent?.key === 'Backspace') {
      if (menuVisible) {
        handleDeleteClick();
      }
    }
  }, [kbEvent]);

  function handleDeleteClick() {
    if (parent.isLocked || editorLocked) return;
    onAreaDeleted(parent);
  }

  useEffect(() => {
    setMenuVisible(activeElem === parent._id);
  }, [activeElem]);

  useEffect(() => {
    if (menuVisible && setActiveElement) {
      setActiveElement(parent._id);
    }
  }, [menuVisible]);

  const renderComponent = () => {
    return (
      <div
        style={{
          ...parent.style,
          minHeight: parent.children && parent.children.length ? 'auto' : 100,
        }}
        className={`feed-row ${parent.className} relative cursor-pointer `}
        onClick={() => {
          if (menuVisible) {
            setMenuVisible(false);
          }
        }}
      >
        <ClickAway onClickAway={() => setMenuVisible(false)} className={parent.className}>
          <div
            onClick={() => {
              setMenuVisible(true);
            }}
            onMouseOver={e => {
              e.stopPropagation();
              setMouseOver(true);
            }}
            onMouseOut={() => {
              setMouseOver(false);
            }}
            className={`absolute z-99 rounded ${menuVisible || mouseOver ? 'outline-row' : ''}`}
            style={{
              height: `calc(100% + 10px)`,
              width: `calc(100% + 10px)`,
              top: -5,
              left: -5,
            }}
          />
          {parent.content && (
            <div>
              <div dangerouslySetInnerHTML={{ __html: parent.content.content }} />
            </div>
          )}
          {parent.contentType !== 'static' &&
            parent.children &&
            parent.children.map((child, i) => {
              if (child.contentType === 'venue-rundown') {
                return (
                  <Fragment key={i}>
                    <VenuesRundownHotspot
                      contentTemplate={{
                        _id: child._id,
                        name: '',
                        content: '',
                        params: child.params,
                        // TODO: Sorry, need to fix this...
                        property: {} as any,
                      }}
                      onAreaDeleted={onAreaDeleted}
                      area={child}
                      selectedLanguage={selectedLanguage}
                      showMergedVars={showMergedVars}
                      highlightAreas={highlightAreas}
                      editorLocked={editorLocked}
                      commonTexts={dailyProgram.collections.commonTexts}
                      mcmKey={mcmKey}
                    />
                  </Fragment>
                );
              }
              if (child.contentType === 'venue-meal-periods') {
                return (
                  <Fragment key={i}>
                    <VenueMealPeriodsHotspot
                      contentTemplate={{
                        _id: child._id,
                        name: '',
                        content: '',
                        params: child.params,
                        // TODO: Sorry, need to fix this...
                        property: {} as any,
                      }}
                      onAreaDeleted={onAreaDeleted}
                      area={child}
                      selectedLanguage={selectedLanguage}
                      showMergedVars={showMergedVars}
                      highlightAreas={highlightAreas}
                      editorLocked={editorLocked}
                      commonTexts={dailyProgram.collections.commonTexts}
                      mcmKey={mcmKey}
                    />
                  </Fragment>
                );
              }
              if (child.contentType === 'column') {
                return (
                  <FeedEditorColumn
                    key={child._id}
                    onAreaDeleted={onAreaDeleted}
                    onElementCreated={onElementCreated}
                    onRowCreated={onRowCreated}
                    onColumnCreated={onColumnCreated}
                    onRowMove={onRowMove}
                    parent={child}
                    parentIdx={i}
                    childCount={(parent.children || []).length}
                    onDrop={ct => onContentChange(child, ct)}
                    onContentChange={onContentChange}
                    onElementChanged={onElementChanged}
                    selectedLanguage={selectedLanguage}
                    showMergedVars={showMergedVars}
                    highlightAreas={highlightAreas}
                    onClickAgain={() => {
                      setMenuVisible(true);
                    }}
                    activeElem={activeElem}
                    setActiveElement={setActiveElement}
                    editorLocked={editorLocked}
                    dailyProgram={dailyProgram}
                    mcmKey={mcmKey}
                  />
                );
              }
              if (child.contentType === 'static') {
                const props = {
                  onContentChange,
                  onRowCreated,
                  onRowMove,
                  onColumnCreated,
                  onElementCreated,
                  onElementChanged,
                  onAreaDeleted,
                  selectedLanguage,
                  showMergedVars,
                  highlightAreas,
                  editorLocked,
                  mcmKey,
                };
                return (
                  <Fragment key={i}>
                    {child.children!.map((subchild, j) => {
                      if (subchild.contentType === 'row') {
                        return (
                          <FeedEditorRow
                            onElementChanged={onElementChanged}
                            onAreaDeleted={onAreaDeleted}
                            onElementCreated={onElementCreated}
                            onRowCreated={onRowCreated}
                            onColumnCreated={onColumnCreated}
                            onRowMove={onRowMove}
                            parent={subchild}
                            parentIdx={j}
                            onDrop={() => {
                              /* NOOP */
                            }}
                            onContentChange={onContentChange}
                            selectedLanguage={selectedLanguage}
                            showMergedVars={showMergedVars}
                            highlightAreas={highlightAreas}
                            editorLocked={editorLocked}
                            dailyProgram={dailyProgram}
                            mcmKey={mcmKey}
                          />
                        );
                      }
                      if (subchild.contentType === 'column') {
                        return (
                          <FeedEditorColumn
                            onAreaDeleted={onAreaDeleted}
                            onElementCreated={onElementCreated}
                            onRowCreated={onRowCreated}
                            onColumnCreated={onColumnCreated}
                            onRowMove={onRowMove}
                            parent={subchild}
                            parentIdx={j}
                            childCount={(child.children || []).length}
                            onDrop={ct => onContentChange(child, ct)}
                            onContentChange={onContentChange}
                            onElementChanged={onElementChanged}
                            selectedLanguage={selectedLanguage}
                            showMergedVars={showMergedVars}
                            highlightAreas={highlightAreas}
                            onClickAgain={() => {
                              setMenuVisible(true);
                            }}
                            editorLocked={editorLocked}
                            dailyProgram={dailyProgram}
                            mcmKey={mcmKey}
                          />
                        );
                      }
                    })}
                    <FeedEditorHotspot isChild noChilds area={child} {...props} />
                  </Fragment>
                );
              }
              if (child.contentType === 'row') {
                return (
                  <Fragment key={i}>
                    <FeedEditorRow
                      onElementChanged={onElementChanged}
                      onAreaDeleted={onAreaDeleted}
                      onElementCreated={onElementCreated}
                      onRowCreated={onRowCreated}
                      onColumnCreated={onColumnCreated}
                      onRowMove={onRowMove}
                      parent={child}
                      parentIdx={i}
                      onDrop={() => {
                        /* NOOP */
                      }}
                      onContentChange={onContentChange}
                      selectedLanguage={selectedLanguage}
                      showMergedVars={showMergedVars}
                      highlightAreas={highlightAreas}
                      editorLocked={editorLocked}
                      dailyProgram={dailyProgram}
                      mcmKey={mcmKey}
                    />
                  </Fragment>
                );
              }
            })}
          <div
            className={`row-layout-actions isRow ${parent.isLocked ? 'bg-grey-dark' : 'bg-green'} ${
              menuVisible ? 'visible' : ''
            }`}
            style={{
              opacity: menuVisible ? 1 : 0,
              pointerEvents: menuVisible ? 'all' : 'none',
            }}
          >
            <div className="inline-flex h-full justify-between items-center px-2 w-full">
              <span className="text-white text-base font-bold mr-3">ROW</span>
              <div className="ml-auto"></div>
              {!parent.isLocked && parentIdx != 0 && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="text-lg px-2-i"
                  onClick={() => {
                    onRowMove(parent, parentIdx, -1);
                  }}
                  icon="arrow-up"
                />
              )}
              {!parent.isLocked && parentIdx != (childCount || 0) - 1 && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="text-lg px-2-i"
                  onClick={() => {
                    onRowMove(parent, parentIdx, 1);
                  }}
                  icon="arrow-down"
                />
              )}
              {area &&
                !area.isLocked &&
                area.contentType !== 'venue-rundown' &&
                area.contentType !== 'venue-meal-periods' &&
                !editorLocked && (
                  <BubbleButton
                    variant="white"
                    inverse
                    className="text-lg px-2-i"
                    onClick={() => {
                      onRowCreated(area, parentIdx + 1, createRow());
                    }}
                    icon="table-rows"
                  />
                )}
              {!parent.isLocked && !editorLocked && (
                <BubbleButton
                  variant="white"
                  inverse
                  className="text-lg px-2-i"
                  onClick={() => {
                    onColumnCreated(parent, (parent.children || []).length || 0, createColumn());
                  }}
                  icon="table-columns"
                />
              )}
              <BubbleButton
                variant="white"
                inverse
                className="text-lg px-2-i"
                disabled={parent.isLocked || editorLocked}
                onClick={() => handleDeleteClick()}
                icon={parent.isLocked || editorLocked ? 'lock' : 'trash-alt'}
              />
            </div>
          </div>
        </ClickAway>
      </div>
    );
  };

  if (parent.children) return renderComponent();
  return connectDropTarget(renderComponent());
};

export default DropTarget<FeedEditorRowProps>(
  CONTENT_DND_TYPE,
  hotspotTarget,
  collectTarget
)(FeedEditorRow);
